import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useNavigate } from 'react-router-dom';
import SitemarkIcon from './SitemarkIcon';
import FooterSitemarkIcon from './FooterSitemarkIcon';
import {
  GlassFooter,
  FooterSection,
  FooterSectionTitle,
  FooterLink,
  FooterLogo,
  FooterCompanyName,
  GlassSocialIcon,
  BodyText,
  GlassFooterContainer,
  GlassFooterColumns,
  GlassFooterColumn,
  GlassFooterColumnTitle,
  GlassFooterColumnLink,
  GlassFooterBottom,
  GlassFooterBranding,
  GlassFooterLogo,
  GlassFooterCompanyText,
  GlassSocialIconsContainer,
  GlassSocialIconButton,
  GlassShadows,
  GlassColors,
} from '../../glassmorphism/GlassmorphismStyles';

// Remove Copyright component as it's not needed in the new design

// Simplified PDF Viewer Component
interface PDFViewerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  pdfUrl: string;
}

function PDFViewer({ open, onClose, title, pdfUrl }: PDFViewerProps) {
  const [showLoader, setShowLoader] = React.useState(true);
  const [showError, setShowError] = React.useState(false);

  // Reset states when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setShowLoader(true);
      setShowError(false);

      // Hide loader after 3 seconds regardless of load state
      const timer = setTimeout(() => {
        setShowLoader(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [open]);

  // Handle iframe load
  const handleLoad = () => {
    console.log('PDF loaded:', pdfUrl);
    setShowLoader(false);
    setShowError(false);
  };

  // Handle iframe error
  const handleError = () => {
    console.error('PDF failed to load:', pdfUrl);
    setShowLoader(false);
    setShowError(true);
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant='h6'>{title}</Typography>
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Loading indicator */}
        {showLoader && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.paper',
              zIndex: 2,
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant='body2' color='text.secondary'>
              Loading PDF...
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {showError && !showLoader && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              gap: 2,
              p: 3,
            }}
          >
            <Alert severity='error' sx={{ maxWidth: '400px' }}>
              <Typography variant='body2'>
                Unable to display PDF in this browser
              </Typography>
            </Alert>
            <Button
              variant='contained'
              href={pdfUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              Open PDF in New Tab
            </Button>
          </Box>
        )}

        {/* PDF iframe - always render */}
        <iframe
          src={pdfUrl}
          width='100%'
          height='100%'
          style={{
            border: 'none',
            minHeight: '500px',
            flex: 1,
            display: showError ? 'none' : 'block',
          }}
          title={title}
          onLoad={handleLoad}
          onError={handleError}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant='outlined'>
          Close
        </Button>
        <Button
          href={pdfUrl}
          target='_blank'
          rel='noopener noreferrer'
          variant='contained'
        >
          Open in New Tab
        </Button>
        <Button href={pdfUrl} download variant='outlined'>
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Footer() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [pdfViewer, setPdfViewer] = React.useState({
    open: false,
    title: '',
    pdfUrl: '',
  });

  // PDF URLs - try different path variations
  const PDF_URLS: Record<string, string> = {
    privacy: '/assets/documents/privacy-policy.pdf', // Original path
    terms: '/assets/documents/terms-of-service.pdf',
  };

  const handleOpenPDF = (type: string, title: string) => {
    const pdfUrl = PDF_URLS[type];

    if (!pdfUrl) {
      console.error(`PDF URL not found for type: ${type}`);
      return;
    }

    console.log(`Opening PDF: ${title} at ${pdfUrl}`);
    setPdfViewer({
      open: true,
      title,
      pdfUrl,
    });
  };

  const handleClosePDF = () => {
    setPdfViewer({
      open: false,
      title: '',
      pdfUrl: '',
    });
  };

  return (
    <>
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          paddingBottom: '24px',
        }}
      >
        <GlassFooterContainer>
          {/* Three Column Layout */}
          {isMobile ? (
            /* Mobile Accordion Layout - Centered */
            <Box
              sx={{
                width: '100%',
                textAlign: 'center',
                padding: '24px',
                borderRadius: '32px',
                boxShadow: GlassShadows.glassDown,
              }}
            >
              {/* Product Accordion */}
              <Accordion
                defaultExpanded={false}
                disableGutters
                elevation={0}
                sx={{
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  border: 'none',
                  marginBottom: '24px',
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? GlassColors.secondary
                            : GlassColors.accent,
                      }}
                    />
                  }
                  sx={{
                    justifyContent: 'center',
                    padding: 0,
                    border: 'none',
                    minHeight: 'unset',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiAccordionSummary-content': {
                      justifyContent: 'center',
                      flexGrow: 'unset',
                      margin: '0',
                      marginRight: '0px !important',
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      color: GlassColors.accent,
                      marginLeft: '5px',
                      marginRight: '0px',
                    },
                  }}
                >
                  <GlassFooterColumnTitle>Product</GlassFooterColumnTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    textAlign: 'center',
                    padding: '8px 0 0 0',
                    border: 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <GlassFooterColumnLink
                      component='a'
                      href='/support'
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/support');
                      }}
                    >
                      FAQs
                    </GlassFooterColumnLink>
                    <GlassFooterColumnLink
                      component='a'
                      href='/sign-up?role=handyman'
                      data-ga-label="Become a Tasker - Footer"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/sign-up?role=handyman');
                      }}
                    >
                      Become a Tasker
                    </GlassFooterColumnLink>
                    <GlassFooterColumnLink
                      component='a'
                      href='/browse-handyman'
                      data-ga-label="Find a Professional - Footer"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/browse-handyman');
                      }}
                    >
                      Find a Professional
                    </GlassFooterColumnLink>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Company Accordion */}
              <Accordion
                defaultExpanded={false}
                disableGutters
                elevation={0}
                sx={{
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  border: 'none',
                  marginBottom: '24px',
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? GlassColors.secondary
                            : GlassColors.accent,
                      }}
                    />
                  }
                  sx={{
                    justifyContent: 'center',
                    padding: 0,
                    border: 'none',
                    minHeight: 'unset',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiAccordionSummary-content': {
                      justifyContent: 'center',
                      margin: '0',
                      marginRight: '0px !important',
                      flexGrow: 'unset',
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      color: GlassColors.accent,
                      marginLeft: '5px',
                      marginRight: '0px',
                    },
                  }}
                >
                  <GlassFooterColumnTitle>Company</GlassFooterColumnTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    textAlign: 'center',
                    padding: '8px 0 16px 0',
                    border: 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <GlassFooterColumnLink
                      component='a'
                      href='/about-us'
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/about-us');
                      }}
                    >
                      About us
                    </GlassFooterColumnLink>
                    <GlassFooterColumnLink
                      component='a'
                      href='/support'
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/support');
                      }}
                    >
                      Support
                    </GlassFooterColumnLink>
                    <GlassFooterColumnLink
                      component='a'
                      href='/contact-us'
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/contact-us');
                      }}
                    >
                      Contact
                    </GlassFooterColumnLink>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Legal Accordion */}
              <Accordion
                defaultExpanded={false}
                disableGutters
                elevation={0}
                sx={{
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  marginBottom: '0 !important',
                  border: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? GlassColors.secondary
                            : GlassColors.accent,
                      }}
                    />
                  }
                  sx={{
                    justifyContent: 'center',
                    padding: 0,
                    border: 'none',
                    minHeight: 'unset',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiAccordionSummary-content': {
                      justifyContent: 'center',
                      margin: '0',
                      flexGrow: 'unset',
                      marginRight: '0px !important',
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                      color: GlassColors.accent,
                      marginLeft: '5px',
                      marginRight: '0px',
                    },
                  }}
                >
                  <GlassFooterColumnTitle>Legal</GlassFooterColumnTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    textAlign: 'center',
                    padding: '8px 0 16px 0',
                    border: 'none',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      alignItems: 'center',
                    }}
                  >
                    <GlassFooterColumnLink
                      component='a'
                      href='/terms-of-service'
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/terms-of-service');
                      }}
                    >
                      Terms of service
                    </GlassFooterColumnLink>
                    <GlassFooterColumnLink
                      component='a'
                      href='/privacy-policy'
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/privacy-policy');
                      }}
                    >
                      Privacy policy
                    </GlassFooterColumnLink>
                    <GlassFooterColumnLink
                      component='a'
                      href='/cookies'
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/cookies');
                      }}
                    >
                      Cookies
                    </GlassFooterColumnLink>
                    <GlassFooterColumnLink
                      component='a'
                      href='/platform-fees'
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/platform-fees');
                      }}
                    >
                      Platform fees
                    </GlassFooterColumnLink>
                    <GlassFooterColumnLink
                      component='a'
                      href='/refund-policy'
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        navigate('/refund-policy');
                      }}
                    >
                      Refund policy
                    </GlassFooterColumnLink>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          ) : (
            /* Desktop Column Layout */
            <GlassFooterColumns>
              {/* Product Column */}
              <GlassFooterColumn>
                <GlassFooterColumnTitle>Product</GlassFooterColumnTitle>
                <GlassFooterColumnLink
                  component='a'
                  href='/support'
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/support');
                  }}
                >
                  FAQs
                </GlassFooterColumnLink>
                <GlassFooterColumnLink
                  component='a'
                  href='/sign-up?role=handyman'
                  data-ga-label="Become a Tasker - Footer"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/sign-up?role=handyman');
                  }}
                >
                  Become a Tasker
                </GlassFooterColumnLink>
                <GlassFooterColumnLink
                  component='a'
                  href='/browse-handyman'
                  data-ga-label="Find a Professional - Footer"
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/browse-handyman');
                  }}
                >
                  Find a Professional
                </GlassFooterColumnLink>
              </GlassFooterColumn>

              {/* Company Column */}
              <GlassFooterColumn>
                <GlassFooterColumnTitle>Company</GlassFooterColumnTitle>
                <GlassFooterColumnLink
                  component='a'
                  href='/about-us'
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/about-us');
                  }}
                >
                  About us
                </GlassFooterColumnLink>
                <GlassFooterColumnLink
                  component='a'
                  href='/support'
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/support');
                  }}
                >
                  Support
                </GlassFooterColumnLink>
                <GlassFooterColumnLink
                  component='a'
                  href='/contact-us'
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/contact-us');
                  }}
                >
                  Contact
                </GlassFooterColumnLink>
              </GlassFooterColumn>

              {/* Legal Column */}
              <GlassFooterColumn>
                <GlassFooterColumnTitle>Legal</GlassFooterColumnTitle>
                <GlassFooterColumnLink
                  component='a'
                  href='/terms-of-service'
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/terms-of-service');
                  }}
                >
                  Terms of service
                </GlassFooterColumnLink>
                <GlassFooterColumnLink
                  component='a'
                  href='/privacy-policy'
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/privacy-policy');
                  }}
                >
                  Privacy policy
                </GlassFooterColumnLink>
                <GlassFooterColumnLink
                  component='a'
                  href='/cookies'
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/cookies');
                  }}
                >
                  Cookies
                </GlassFooterColumnLink>
                <GlassFooterColumnLink
                  component='a'
                  href='/platform-fees'
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/platform-fees');
                  }}
                >
                  Platform fees
                </GlassFooterColumnLink>
                <GlassFooterColumnLink
                  component='a'
                  href='/refund-policy'
                  onClick={(e: React.MouseEvent) => {
                    e.preventDefault();
                    navigate('/refund-policy');
                  }}
                >
                  Refund policy
                </GlassFooterColumnLink>
              </GlassFooterColumn>
            </GlassFooterColumns>
          )}

          {/* Footer Bottom */}
          <GlassFooterBottom>
            {/* Company Branding */}
            <GlassFooterBranding>
              <GlassFooterLogo>
                <Box
                  component='img'
                  src='/logo-blue.svg'
                  alt='Word of Mouth LTD Logo'
                  sx={{
                    width: { xs: '40px', sm: '64px' },
                    height: { xs: '40px', sm: '64px' },
                    display: 'block',
                  }}
                />
              </GlassFooterLogo>
              <GlassFooterCompanyText>Word of Mouth LTD</GlassFooterCompanyText>
            </GlassFooterBranding>

            {/* Social Media Icons */}
            <GlassSocialIconsContainer>
              <GlassSocialIconButton
                onClick={() =>
                  window.open(
                    'https://www.instagram.com/womapp.uk/',
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
                aria-label='Instagram'
              >
                <InstagramIcon />
              </GlassSocialIconButton>
              <GlassSocialIconButton
                onClick={() =>
                  window.open(
                    'https://www.facebook.com/womapp.uk/',
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
                aria-label='Facebook'
              >
                <FacebookIcon />
              </GlassSocialIconButton>
              <GlassSocialIconButton
                onClick={() =>
                  window.open(
                    'https://www.linkedin.com/company/word-of-mouth-ltd/',
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
                aria-label='LinkedIn'
              >
                <LinkedInIcon />
              </GlassSocialIconButton>
            </GlassSocialIconsContainer>
          </GlassFooterBottom>
        </GlassFooterContainer>
      </Container>

      {/* PDF Viewer Modal */}
      <PDFViewer
        open={pdfViewer.open}
        onClose={handleClosePDF}
        title={pdfViewer.title}
        pdfUrl={pdfViewer.pdfUrl}
      />
    </>
  );
}
